import React, { useEffect, useState } from "react";
import { setJourneyToken } from "@/lib/journeyToken";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Div100vh from "react-div-100vh";
import OTPInput from "react-otp-input";
import {
  isValidPhone,
  getDataB2B,
  formatINR,
  toAbsoluteJourneyUrl,
  getUserFacingErrorMessage,
} from "@/lib/helper";
import PrimaryButton from "../components/PrimaryButton";
import GoBackButton from "@/components/common/buttons/GoBackButton";
import { verifyOtpB2B, loginLeadB2B, verifyOtp } from "@/lib/apis";
import config from "@/config";
import CustomDrawer from "@/components/common/drawer";
import Header from "../components/Header";
import TextField from "@/components/common/Input/TextField";
import {
  ArrowRight,
  ChevronLeftIcon,
  MessageCircleQuestionIcon,
} from "lucide-react";
// import { cn } from "@/lib/utils";
import {
  PACKAGE_PARTNERS,
  PARTNER_LEAD_TYPES,
  getWelcomeSubheading,
} from "../config/partnerJourney";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

const renderSelector = (data, index) => {
  const { item, state } = data;
  const { type, value, label, icon } = item;
  const { selected, setSelected, accentColor = "#1987fb" } = state;

  const selectorVariants = cva(
    "flex items-center justify-center gap-2 border rounded-lg cursor-pointer transition-all duration-300 ease-in-out",
    {
      variants: {
        type: {
          card: "p-4 w-36 h-21 flex-col text-center",
          chip: "px-4 py-2 text-sm",
        },
        isSelected: {
          true: cn(
            `bg-[${accentColor}]/30 text-[${accentColor}] ]shadow-md`,
            `border-[${accentColor}]`,
          ),
          false: "border-gray-300 bg-white hover:border-gray-400",
        },
      },
      defaultVariants: {
        isSelected: false,
      },
    },
  );

  return (
    <div
      key={index}
      className={selectorVariants({
        type,
        isSelected: selected?.value === value,
      })}
      onClick={() => setSelected(item)}
    >
      {icon}
      <span className="uppercase">{label}</span>
    </div>
  );
};

export default function PartnerSignUp({ theme }) {
  console.log(theme, "theme");
  const location = useLocation();
  const navigate = useNavigate();
  const { userIdentifier } = useParams();

  const { phone: initialPhone, name } = location.state || {};
  const [selectedLeadType, setSelectedLdadType] = useState(
    PARTNER_LEAD_TYPES?.[theme?.name]?.find((item) => item?.default),
  );

  const [formData, setFormData] = useState({
    phone: initialPhone || "",
    first_name: "",
    last_name: "",
    rent: "",
    ...(selectedLeadType ? { type: selectedLeadType?.value || null } : {}),
    // ...(theme?.name === "fabhostels" ? { type: "student" } : {}),
  });

  const [phone, setPhone] = useState(initialPhone || "");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpDrawer, setOtpDrawer] = useState(false);
  const [error, setError] = useState("");
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [newOtpSent, setNewOtpSent] = useState(false);

  const [isChecked, setIsChecked] = useState(true);
  const [linkDrawer, setLinkDrawer] = useState(false);
  const [currLink, setCurrLink] = useState("");

  const [b2CJourneyLink, setB2CJourneyLink] = useState("");

  const handleLeadTypeSelectorClick = (value) => {
    setSelectedLdadType(value);
    setFormData((prev) => {
      return {
        ...prev,
        type: value?.value,
      };
    });
  };

  useEffect(() => {
    if (otp.length === config.OTP_LENGTH) {
      handleVerifyOtp();
    }
  }, [otp]);

  const handleFormSubmit = async () => {
    try {
      setError("");
      setLoading(true);

      // useParams() is always synchronously correct for this route; localStorage is only a
      // fallback in case this component is ever reused off its usual /p/:userIdentifier mount
      // — see SignUpScreen.jsx for the incident this pattern was built to prevent.
      const response = initialPhone
        ? await getDataB2B(
            "b2b/onboard/verify-token",
            userIdentifier || localStorage.getItem("user_identifier"),
          )
        : await loginLeadB2B({
            ...formData,
            // Attribute the lead to the partner from the URL (works for any
            // dashboard-created partner); fall back to the theme name.
            source: userIdentifier || theme.name,
          });

      if (response?.status === "success" || response?.success) {
        if (response.sessionUrl) {
          //console.log(response)
          // Sanitize before redirecting: a missing backend BASE_FE_URL makes
          // sessionUrl a relative "undefined/b/<id>" that would otherwise resolve
          // against the current /p/<partner> path and 404. See toAbsoluteJourneyUrl.
          window.location.href = toAbsoluteJourneyUrl(response.sessionUrl);
          return;
          // setB2CJourneyLink(response.sessionUrl);
        }
        setOtpDrawer(true);
      } else {
        setError(getUserFacingErrorMessage(response));
      }
    } catch (error) {
      console.error("Error in handleFormSubmit:", error);
      setError(
        "Something went wrong on our end. Please try again in a moment — if it keeps happening, contact support."
      );
    } finally {
      setLoading(false);
    }
  };

  const userAgent = navigator.userAgent;

  // useEffect(() => {
  //   if (isValidPhone(phone) && !otpDrawer) handleFormSubmit();
  // }, [phone]);

  const handleResendOtp = async () => {
    setOtp("");
    setVerifying(true);
    await handleFormSubmit();
    setOtpError("");
    setNewOtpSent(true);
    setVerifying(false);
  };

  const handleVerifyOtp = async () => {
    if (otp.length < config.OTP_LENGTH) {
      setOtpError("Please enter the complete OTP.");
      return;
    }

    try {
      setVerifying(true);
      const response = initialPhone
        ? await verifyOtpB2B({ otp }, "b2b/onboard/verify-otp", userIdentifier)
        : await verifyOtp({
            otp: otp,
          });

      if (response?.status === false || response.success === false) {
        setOtpError("Invalid OTP. Please try again.");
      } else {
        // Reliable useParams() value for the auth header (same fix as the OTP-send call
        // above); a SEPARATELY-named local for the localStorage read the navigate() below
        // already relied on, so that call's behavior is untouched.
        const storedUserIdentifier = localStorage.getItem("user_identifier");
        await fetch(`${config.API_URL}/b2b/onboard/check-eligibility`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Short ${userIdentifier || storedUserIdentifier}`,
          },
        });

        if (b2CJourneyLink) {
          window.location.href = b2CJourneyLink;
          return;
        }
        if (!initialPhone)
          setJourneyToken(response.token);

        // `?step=loading` says "we just authenticated — now work out where this person goes".
        // A bare `/p/<partner>` no longer does that, because it cannot tell this case apart from a
        // stranger opening the partner's public link on a device with a leftover session.
        navigate(`/p/${storedUserIdentifier}?step=loading`);
      }
    } catch (error) {
      console.error("Error in handleVerifyOtp:", error);
      setOtpError("Verification failed. Try again.");
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    if (formData) {
      console.log(formData, "formData");
    }
  }, [formData]);

  if (/android/i.test(userAgent)) {
    return (
      <div
        className={`bg-[${theme?.bgColor}] ${theme?.textColor} p-12 flex flex-col justify-between h-screen overflow-y-scroll`}
      >
        <div className="flex flex-col gap-4">
          <Header
            heading={name ? `Hey ${name} 👋🏻` : "Welcome 👋🏻"}
            subheading={getWelcomeSubheading(userIdentifier)}
            showBackButton={false}
          />

          <div className="flex gap-2">
            <TextField
              name="first_name"
              label="First Name"
              value={formData.first_name || ""}
              onChange={(val) => setFormData({ ...formData, first_name: val })}
              placeholder="First Name"
              type="text"
              required
            />
            <TextField
              name="last_name"
              label="Last Name"
              value={formData.last_name || ""}
              onChange={(val) => setFormData({ ...formData, last_name: val })}
              placeholder="Last Name"
              type="text"
              required
            />
          </div>
          <TextField
            name="phone"
            label="Phone Number"
            value={formData.phone}
            onChange={(val) => setFormData({ ...formData, phone: val })}
            placeholder="Enter your phone number"
            disabled={!!initialPhone}
            type="tel"
            required
            maxLength={10}
          />

          {PACKAGE_PARTNERS?.[theme?.name] ? (
            <select
              defaultValue={""}
              name="rent"
              className={`border p-4 text-base border-primary-500 ${
                theme?.backgroundColor || "bg-input-bg"
              } ${
                theme?.textColor || "text-black"
              } focus:outline-none w-full h-full placeholder-transparent peer rounded-none peer`}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  rent: Math.round(
                    Number(e.target.value) /
                      PACKAGE_PARTNERS?.[theme?.name]?.defaultTenure,
                  ),
                  package_amount: Number(e.target.value),
                })
              }
              required
            >
              <option disabled value="">
                Select Package amount
              </option>
              {PACKAGE_PARTNERS?.[theme?.name]?.packages?.map((item) => (
                <option value={item}>Package amount - {formatINR(item)}</option>
              ))}
            </select>
          ) : (
            <TextField
              name="rent"
              label="Approximate Rent (monthly)"
              value={formData.rent}
              onChange={(val) =>
                parseInt(val || 0) < 500000 &&
                setFormData({ ...formData, rent: val })
              }
              placeholder="Enter your (approximate rent)"
              type="number"
              required
              pattern="[0-9]*"
              hint={
                parseInt(formData?.rent) < 4999
                  ? "Rent must be >= 5000"
                  : parseInt(formData?.rent) > 500000
                    ? "Rent must be < 500000"
                    : ""
              }
            />
          )}
        </div>

        {error && (
          <div className="w-full p-2 px-4 bg-red-400/10 text-red-400 rounded-lg">
            {error}
          </div>
        )}

        {theme?.checkChargesUrl && (
          <div className="w-full mt-8 font-medium">
            <a
              className="w-full"
              target="_blank"
              referrer="circlepe.club"
              href={theme?.checkChargesUrl}
            >
              <div className="w-full flex items-center capitalize border-b text-base py-3 justify-between">
                <div className="flex items-center  gap-x-2 text-center">
                  <div>
                    <MessageCircleQuestionIcon
                      className={`text-[${theme?.primaryButtonBgColorCode}]`}
                    />
                  </div>
                  <div className="text-base">Check the calculations</div>
                </div>

                <div>
                  <ArrowRight />
                </div>
              </div>
            </a>
          </div>
        )}

        <CustomDrawer isOpen={otpDrawer} theme={theme}>
          <div className="p-10">
            <GoBackButton customBackSubmit={() => setOtpDrawer(false)} />
            <div className={`w-full text-left ${theme?.textColor}`}>
              <div className={`heading mb-2 mt-12 ${theme.headingColor}`}>
                OTP Verification
              </div>
              <div className={`subheading text-sm ${theme?.textColor}`}>
                OTP Sent to <span>{formData.phone}</span>
              </div>
            </div>
            <div className="flex-grow overflow-auto mt-5">
              <OTPInput
                value={otp}
                numInputs={config.OTP_LENGTH}
                onChange={setOtp}
                isInputNum={true}
                containerStyle={{ display: "flex", justifyContent: "center" }}
                renderInput={(props) => (
                  <input
                    {...props}
                    type="tel"
                    style={{ border: "1px solid #444", margin: "4px" }}
                    className="border-[#999] border w-10 text-center h-12 text-black text-lg bg-transparent"
                  />
                )}
              />
              {otpError && (
                <div className="w-full p-2 px-4 bg-red-400/10 text-red-400 rounded-lg">
                  {otpError}
                </div>
              )}
              {!verifying && (
                <div className="w-full p-4 text-center subheading text-sm">
                  <span>
                    {newOtpSent
                      ? "New OTP sent, try again if not received."
                      : "Didn't get the code?"}
                  </span>{" "}
                  <span
                    onClick={handleResendOtp}
                    className="font-semibold cursor-pointer text-[#1987fb]"
                  >
                    Resend it
                  </span>
                </div>
              )}
            </div>
            <div className="pt-6">
              <PrimaryButton
                text="Verify"
                handleClick={handleVerifyOtp}
                loading={verifying}
                disabled={otp.length < config.OTP_LENGTH}
              />
            </div>
          </div>
        </CustomDrawer>

        <CustomDrawer isOpen={linkDrawer} theme={theme}>
          {/* <GoBackButton customBackSubmit={()=>setLinkDrawer(false)}/> */}

          <div className="h-full w-full absolute left-0 top-0">
            <iframe
              src={currLink}
              title="Website Frame"
              className="w-full h-full rounded-xl border border-[#444] my-4"
              allowFullScreen
            ></iframe>
          </div>
          <button
            onClick={() => setLinkDrawer(false)}
            className="absolute top-8 left-8 p-2 cursor-pointer hover:bg-slate-100 se:w-8 se:h-8 sm:h-11 sm:w-11 bg-white rounded-full flex flex-col justify-center items-center"
          >
            <ChevronLeftIcon className="size-112  text-black" />
          </button>
        </CustomDrawer>

        <div className="flex flex-col items-center gap-2 mt-10 h-full justify-end">
          {/* consent */}
          <div className="flex items-start space-x-2 text-3xs mb-4">
            {/* Checkbox */}

            {/* Label */}
            <label
              htmlFor="termsCheckbox"
              className=" text-[#999]  leading-relaxed"
            >
              {/* If you are chaning below consent, change this as well: CONSENT_MESSAGE */}
              By continuing, I hereby agree to{" "}
              <a
                // href="/terms-and-conditions"
                // target="_blank"
                // rel="noopener noreferrer"
                onClick={() => {
                  setCurrLink("https://www.circlepe.club/terms-conditions");
                  setLinkDrawer(true);
                }}
                className="underline hover:text-primary cursor-pointer"
              >
                CirclePe's T&Cs
              </a>{" "}
              and{" "}
              <a
                // href="/privacy-policy"
                // target="_blank"
                // rel="noopener noreferrer"
                onClick={() => {
                  setCurrLink("https://www.circlepe.club/privacy-policy");
                  setLinkDrawer(true);
                }}
                className="underline hover:text-primary cursor-pointer"
              >
                Privacy Policy
              </a>{" "}
              & consent to be contacted by CirclePe and its partners via
              whatsapp, etc.
            </label>
          </div>
        </div>

        <PrimaryButton
          text="Submit"
          handleClick={handleFormSubmit}
          loading={loading}
          disabled={
            formData.first_name?.length < 3 ||
            formData.last_name?.length < 1 ||
            formData?.phone?.length < 10 ||
            parseInt(formData.rent || 0) < 4999 ||
            parseInt(formData?.rent || 0) > 500000
          }
        />

        {/* <div className="w-full text-center text-[0.678rem] mt-2">
          powered by{" "}
          <a
            target="_blank"
            href="https://fexprime.com"
            className="hover:underline hover:text-primary"
          >
            Fexprime
          </a>
        </div> */}
      </div>
    );
  } else {
    return (
      <Div100vh
        className={`bg-[${theme?.bgColor}] ${theme?.textColor} p-8 flex flex-col justify-between`}
      >
        <div className="flex flex-col gap-4">
          <Header
            heading={name ? `Hey ${name} 👋🏻` : "Welcome 👋🏻"}
            subheading={getWelcomeSubheading(userIdentifier)}
            showBackButton={false}
          />

          <div className="grid gap-2 grid-cols-2">
            <TextField
              name="first_name"
              label="First Name"
              value={formData.first_name || ""}
              onChange={(val) => setFormData({ ...formData, first_name: val })}
              placeholder="First Name"
              type="text"
            />
            <TextField
              name="last_name"
              label="Last Name"
              value={formData.last_name || ""}
              onChange={(val) => setFormData({ ...formData, last_name: val })}
              placeholder="Last Name"
              type="text"
            />
          </div>
          <TextField
            name="phone"
            label="Phone Number"
            value={formData.phone}
            onChange={(val) => setFormData({ ...formData, phone: val })}
            placeholder="Enter your phone number"
            disabled={!!initialPhone}
            type="tel"
            required
            maxLength={10}
          />
          {PACKAGE_PARTNERS?.[theme?.name] ? (
            <select
              defaultValue={""}
              name="rent"
              className={`border p-4 text-base border-primary-500 ${
                theme?.backgroundColor || "bg-input-bg"
              } ${
                theme?.textColor || "text-black"
              } focus:outline-none w-full h-full placeholder-transparent peer rounded-none peer`}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  rent: Math.round(
                    Number(e.target.value) /
                      PACKAGE_PARTNERS?.[theme?.name]?.defaultTenure,
                  ),
                  package_amount: Number(e.target.value),
                })
              }
              required
            >
              <option disabled value="">
                Select Package amount
              </option>
              {PACKAGE_PARTNERS?.[theme?.name]?.packages?.map((item) => (
                <option value={item}>Package amount - {formatINR(item)}</option>
              ))}
            </select>
          ) : (
            <TextField
              name="rent"
              label="Approximate Rent (monthly)"
              value={formData.rent}
              onChange={(val) =>
                parseInt(val || 0) < 500000 &&
                setFormData({ ...formData, rent: val })
              }
              placeholder="Enter your (approximate rent)"
              type="number"
              required
              pattern="[0-9]*"
              hint={
                parseInt(formData?.rent) < 4999
                  ? "Rent must be >= 5000"
                  : parseInt(formData?.rent) > 500000
                    ? "Rent must be < 500000"
                    : ""
              }
            />
          )}

          {PARTNER_LEAD_TYPES?.[theme.name]?.length > 0 ? (
            <div className="flex gap-2 flex-wrap">
              {PARTNER_LEAD_TYPES?.[theme.name]?.map((item, index) =>
                renderSelector(
                  {
                    item,
                    state: {
                      selected: selectedLeadType,
                      setSelected: handleLeadTypeSelectorClick,
                      accentColor: theme?.primaryButtonBgColorCode,
                    },
                  },
                  index,
                ),
              )}
            </div>
          ) : null}
        </div>

        {error && (
          <div className="w-full p-2 px-4 bg-red-400/10 text-red-400 rounded-lg">
            {error}
          </div>
        )}

        {theme?.checkChargesUrl && (
          <div className="w-full mt-8 font-medium">
            <a
              className="w-full"
              target="_blank"
              referrer="circlepe.club"
              href={theme?.checkChargesUrl}
            >
              <div className="w-full flex items-center capitalize border-b text-base py-3 justify-between">
                <div className="flex items-center  gap-x-2 text-center">
                  <div>
                    <MessageCircleQuestionIcon
                      className={`text-[${theme?.primaryButtonBgColorCode}]`}
                    />
                  </div>
                  <div className="text-base">Check the calculations</div>
                </div>

                <div>
                  <ArrowRight />
                </div>
              </div>
            </a>
          </div>
        )}

        <CustomDrawer isOpen={otpDrawer} theme={theme}>
          <div className="p-10">
            <GoBackButton customBackSubmit={() => setOtpDrawer(false)} />
            <div className={`w-full text-left ${theme?.textColor}`}>
              <div className={`heading mb-2 mt-12 ${theme.headingColor}`}>
                OTP Verification
              </div>
              <div className={`subheading text-sm ${theme?.textColor}`}>
                OTP Sent to <span>{formData.phone}</span>
              </div>
            </div>
            <div className="flex-grow overflow-auto mt-5">
              <OTPInput
                value={otp}
                numInputs={config.OTP_LENGTH}
                onChange={setOtp}
                isInputNum={true}
                containerStyle={{ display: "flex", justifyContent: "center" }}
                renderInput={(props) => (
                  <input
                    {...props}
                    type="tel"
                    style={{ border: "1px solid #444", margin: "4px" }}
                    className="border-[#999] border w-10 text-center h-12 text-black text-lg bg-transparent"
                  />
                )}
              />
              {otpError && (
                <div className="w-full p-2 px-4 bg-red-400/10 text-red-400 rounded-lg">
                  {otpError}
                </div>
              )}
              {!verifying && (
                <div className="w-full p-4 text-center subheading text-sm">
                  <span>
                    {newOtpSent
                      ? "New OTP sent, try again if not received."
                      : "Didn't get the code?"}
                  </span>{" "}
                  <span
                    onClick={handleResendOtp}
                    className="font-semibold cursor-pointer text-[#1987fb]"
                  >
                    Resend it
                  </span>
                </div>
              )}
            </div>
            <div className="pt-6">
              <PrimaryButton
                text="Verify"
                handleClick={handleVerifyOtp}
                loading={verifying}
                disabled={otp.length < config.OTP_LENGTH}
              />
            </div>
          </div>
        </CustomDrawer>

        <CustomDrawer isOpen={linkDrawer} theme={theme}>
          {/* <GoBackButton customBackSubmit={()=>setLinkDrawer(false)}/> */}

          <div className="h-full w-full absolute left-0 top-0">
            <iframe
              src={currLink}
              title="Website Frame"
              className="w-full h-full rounded-xl border border-[#444] my-4"
              allowFullScreen
            ></iframe>
          </div>
          <button
            onClick={() => setLinkDrawer(false)}
            className="absolute top-8 left-8 p-2 cursor-pointer hover:bg-slate-100 se:w-8 se:h-8 sm:h-11 sm:w-11 bg-white rounded-full flex flex-col justify-center items-center"
          >
            <ChevronLeftIcon className="size-112  text-black" />
          </button>
        </CustomDrawer>

        <div className="flex flex-col items-center gap-2 mt-10 h-full justify-end">
          {/* consent */}
          <div className="flex items-start space-x-2 text-3xs mb-4">
            {/* Checkbox */}

            {/* Label */}
            <label
              htmlFor="termsCheckbox"
              className=" text-[#999]  leading-relaxed"
            >
              {/* If you are chaning below consent, change this as well: CONSENT_MESSAGE */}
              By continuing, I hereby agree to{" "}
              <a
                // href="/terms-and-conditions"
                // target="_blank"
                // rel="noopener noreferrer"
                onClick={() => {
                  setCurrLink("https://www.circlepe.club/terms-conditions");
                  setLinkDrawer(true);
                }}
                className="underline hover:text-primary cursor-pointer"
              >
                CirclePe's T&Cs
              </a>{" "}
              and{" "}
              <a
                // href="/privacy-policy"
                // target="_blank"
                // rel="noopener noreferrer"
                onClick={() => {
                  setCurrLink("https://www.circlepe.club/privacy-policy");
                  setLinkDrawer(true);
                }}
                className="underline hover:text-primary cursor-pointer"
              >
                Privacy Policy
              </a>{" "}
              & consent to be contacted by CirclePe and its partners via
              whatsapp, etc.
            </label>
          </div>
        </div>

        <PrimaryButton
          text="Submit"
          handleClick={handleFormSubmit}
          loading={loading}
          disabled={
            formData.first_name?.length < 3 ||
            formData.last_name?.length < 1 ||
            formData?.phone?.length < 10 ||
            parseInt(formData.rent || 0) < 4999 ||
            parseInt(formData?.rent || 0) > 500000
          }
        />

        {/* <div className="w-full text-center text-[0.678rem] mt-2">
          powered by{" "}
          <a
            target="_blank"
            href="https://fexprime.com"
            className="hover:underline hover:text-primary"
          >
            Fexprime
          </a>
        </div> */}
      </Div100vh>
    );
  }
}
